import { useQuery } from '@tanstack/react-query';
import { mediaService } from '@/services/media.service';

/**
 * Poll video processing status for a freshly-uploaded video.
 *
 * Starts polling immediately and stops automatically when the backend reports
 * READY (success) or FAILED (terminal error). Returns the live query result so
 * callers can render an appropriate overlay.
 *
 * @param uploadId - The media upload ID returned by the direct-upload endpoint.
 *                   Pass null / undefined to disable polling.
 * @param enabled  - Additional boolean gate (e.g. set to false once parent has
 *                   already applied the thumbnail).
 */
export function useVideoProcessingPoll(
  uploadId: string | null | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['video-processing', uploadId],
    queryFn: () => mediaService.getProcessingStatus(uploadId!),
    enabled: !!uploadId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // READY = transcoding + thumbnail generation complete; FAILED = unrecoverable
      if (status === 'READY' || status === 'FAILED') {
        return false; // stop polling
      }
      return 2000; // poll every 2 seconds while PENDING / UPLOADING / COMPLETED / PROCESSING
    },
    // Always re-fetch — never serve a stale "processing" snapshot
    staleTime: 0,
  });
}
